import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2, CreditCard } from "lucide-react";
import api, { apiErr, money } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const toNum = (v) => (typeof v === "string" ? parseFloat(v) : v) || 0;

export default function PayOnline({ rid }) {
  const [opts, setOpts] = useState(null);
  const [pending, setPending] = useState(null); // confirmation state
  const [busy, setBusy] = useState(false);

  const loadOpts = () =>
    api.get(`/payments/portal/reservations/${rid}/options`).then((r) => setOpts(r.data)).catch(() => setOpts(false));
  useEffect(() => { loadOpts(); }, [rid]);

  if (!opts || opts.enabled === false) return null;

  const hasBalance = opts.balance_cents > 0;
  const hasDeposit = opts.deposits_enabled && opts.deposit_due_cents > 0;
  const requests = opts.requests || [];
  if (!hasBalance && !hasDeposit && requests.length === 0) return null;

  const balance = toNum(opts.balance);
  const openConfirm = (o) => {
    const amt = toNum(o.amount);
    const remainingAfter = Math.max(0, balance - amt);
    setPending({ ...o, amountNum: amt, remainingAfter });
  };

  const confirmPay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/payments/portal/reservations/${rid}/session`,
        { purpose: pending.purpose, request_id: pending.request_id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };

  const Btn = ({ id, label, amount, purpose, request_id }) => (
    <button
      data-testid={`pay-online-${id}`}
      onClick={() => openConfirm({ id, label, amount, purpose, request_id })}
      className="btn-gold px-5 py-3 text-sm flex items-center justify-between gap-3 w-full"
    >
      <span className="flex items-center gap-2"><CreditCard size={16} /> {label}</span>
      <span className="font-medium">{money(amount)}</span>
    </button>
  );

  return (
    <div className="glass rounded-lg p-6 mt-4" data-testid="pay-online-panel">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={18} className="text-gold" />
        <h3 className="font-serif text-xl">Pay Online</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Complete your payment securely. You'll be taken to a secure payment page.</p>
      <div className="space-y-2.5">
        {hasDeposit && <Btn id="deposit" label="Pay Required Deposit" amount={opts.deposit_due} purpose="deposit" />}
        {hasBalance && <Btn id="balance" label="Pay Remaining Balance" amount={opts.balance} purpose="balance" />}
        {requests.map((rq) => (
          <Btn key={rq.id} id={`request-${rq.id}`} label={rq.purpose_label || "Pay Approved Amount"} amount={rq.amount} purpose="request" request_id={rq.id} />
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground mt-4">Card details are entered only on the secure payment page — The D never sees or stores your full card number.</p>

      <Dialog open={!!pending} onOpenChange={(o) => { if (!o && !busy) setPending(null); }}>
        <DialogContent className="bg-surface border-white/10" data-testid="pay-confirm-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Confirm Payment</DialogTitle></DialogHeader>
          {pending && (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Purpose</span><span data-testid="pay-confirm-purpose">{pending.label}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Reservation</span><span>{opts.confirmation_number || "This reservation"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Folio total</span><span>{money(opts.total)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Amount already paid</span><span>{money(opts.paid)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Current balance</span><span>{money(opts.balance)}</span></div>
              <div className="gold-line my-1" />
              <div className="flex justify-between text-base font-medium"><span>Amount to pay</span><span className="text-gold" data-testid="pay-confirm-amount">{money(pending.amount)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Remaining balance after payment</span><span data-testid="pay-confirm-remaining">{money(pending.remainingAfter)}</span></div>
              <div className="flex items-center gap-2 rounded-md bg-white/5 border border-white/10 p-2.5 mt-2">
                <ShieldCheck size={15} className="text-gold shrink-0" />
                <span className="text-xs text-muted-foreground">Secure Payment — you'll enter card details on a secure payment page. The D never stores your card number.</span>
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={confirmPay} disabled={busy} className="btn-gold px-5 py-2.5 text-sm flex-1 flex items-center justify-center gap-2" data-testid="pay-confirm-continue">
                  {busy ? <Loader2 className="animate-spin" size={15} /> : <CreditCard size={15} />} Continue to Secure Payment
                </button>
                <button onClick={() => setPending(null)} disabled={busy} className="btn-ghost-gold px-5 py-2.5 text-sm" data-testid="pay-confirm-cancel">Cancel</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
